import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Lock, Crown, ArrowLeft, Send, Calendar, Flame, Trash2, Eye, ExternalLink, Share2, Copy, Check, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { parseEmbed, withAutoplay } from "@/lib/embed";
import StatusBadge from "@/components/StatusBadge";
import ProgressBar from "@/components/ProgressBar";
import VideoCard from "@/components/VideoCard";
import CommentList from "@/components/CommentList";
import { PatreonBadge } from "@/components/UserBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function parseEmbedLocal() {} // unused, see /lib/embed.js

export default function VideoDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [route, setRoute] = useState(null);
  const [sameRoute, setSameRoute] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [relatedTab, setRelatedTab] = useState("all"); // "all" | "route"
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [patreonUrl, setPatreonUrl] = useState("https://patreon.com/c/SavageUncensored");

  const load = async () => {
    try {
      const { data } = await api.get(`/videos/${id}`);
      setVideo(data);
      const [routeR, sameR, allR, comR, statsR] = await Promise.all([
        api.get(`/routes/${data.route_id}`).catch(() => null),
        api.get("/videos", { params: { route_id: data.route_id, limit: 12 } }).catch(() => null),
        api.get("/videos", { params: { sort: "views", limit: 12 } }).catch(() => null),
        api.get(`/videos/${id}/comments`).catch(() => null),
        api.get("/stats").catch(() => null),
      ]);
      if (routeR) setRoute(routeR.data);
      if (sameR) setSameRoute(sameR.data.filter((v) => v.id !== id).slice(0, 6));
      if (allR) setAllVideos(allR.data.filter((v) => v.id !== id).slice(0, 6));
      if (comR) setComments(comR.data);
      if (statsR) setPatreonUrl(statsR.data.patreon_url);
    } catch (e) {
      toast.error("Failed to load video");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onLike = async () => {
    if (!user) { toast.error("Login to like videos"); navigate("/login"); return; }
    try {
      const { data } = await api.post(`/videos/${id}/like`);
      setVideo((v) => ({ ...v, likes: data.likes, liked_by_user: data.liked, status: data.likes >= v.like_goal && v.status === "unlock_in_progress" ? "unlocked" : v.status }));
      const awarded = data?.xp?.awarded || 0;
      if (awarded > 0) {
        toast.success(`+${awarded} XP • Liked video`, { icon: "⚡", duration: 2000 });
      }
      if (data?.xp?.level_up) {
        toast.success(`Level up! You're now Lv ${data.xp.level}`, { icon: "🏆", duration: 3500 });
      }
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onShare = async () => {
    const canonical = `${window.location.origin}/videos/${id}`;
    // Use the backend share endpoint (with proper OG tags) for the shared URL, so social
    // previews render richly. Humans get redirected to the canonical video page instantly.
    const shareUrl = `${window.location.origin}/api/share/video/${id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: video?.title || "SavageBoy", text: video?.description || "", url: shareUrl });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied!", { icon: "🔗", description: "Rich preview enabled on WhatsApp, Twitter, etc." });
    } catch {
      try { await navigator.clipboard.writeText(canonical); toast.success("Link copied"); }
      catch { toast.error("Could not copy link"); }
    }
  };

  const onComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Login to comment"); return; }
    if (!commentText.trim()) return;
    try {
      const { data } = await api.post(`/videos/${id}/comments`, { text: commentText.trim() });
      const { xp, ...comment } = data;
      setComments((c) => [comment, ...c]);
      setCommentText("");
      const awarded = xp?.awarded || 0;
      if (awarded > 0) toast.success(`+${awarded} XP • Commented`, { icon: "⚡", duration: 2000 });
      if (xp?.level_up) toast.success(`Level up! You're now Lv ${xp.level}`, { icon: "🏆", duration: 3500 });
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDeleteComment = async (cid) => {
    try {
      await api.delete(`/comments/${cid}`);
      setComments((cs) => cs.filter((c) => c.id !== cid));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onReply = async (parent_id, text) => {
    if (!user) { toast.error("Login to reply"); return null; }
    if (!text?.trim()) return null;
    try {
      const { data } = await api.post(`/videos/${id}/comments`, { text: text.trim(), parent_id });
      const { xp, ...comment } = data;
      setComments((c) => [comment, ...c]);
      const awarded = xp?.awarded || 0;
      if (awarded > 0) toast.success(`+${awarded} XP • Replied`, { icon: "⚡", duration: 2000 });
      if (xp?.level_up) toast.success(`Level up! You're now Lv ${xp.level}`, { icon: "🏆", duration: 3500 });
      await refresh();
      return comment;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  };

  const onReact = async (cid, emoji) => {
    if (!user) { toast.error("Login to react"); return; }
    try {
      const { data } = await api.post(`/comments/${cid}/react`, { emoji });
      setComments((cs) => cs.map((c) => (c.id === cid ? { ...c, reactions: data.reactions } : c)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!video) return <div className="max-w-7xl mx-auto px-4 py-24 text-zinc-400">Loading...</div>;

  const embed = parseEmbed(video.youtube_url) || parseEmbed(video.external_url);
  const embedSrc = embed ? withAutoplay(embed) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={`video-detail-${id}`}>
      <Link to="/videos" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to videos
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video">
            {video.status === "patreon_exclusive" ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center p-8 bg-gradient-to-b from-black via-[#0a0203] to-black">
                <img src={video.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="relative">
                  <div className="w-20 h-20 mx-auto rounded-full bg-[#E50914]/20 border border-[#E50914]/40 flex items-center justify-center pulse-red">
                    <Lock className="w-9 h-9 text-[#E50914]" />
                  </div>
                  <h3 className="font-display font-black text-2xl sm:text-3xl mt-5 mb-2">Patreon Exclusive</h3>
                  <p className="text-zinc-300 max-w-md mx-auto text-sm">
                    This uncensored release is available right now for Patreon supporters only.
                  </p>
                  <a
                    href={video.patreon_url || patreonUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="video-patreon-btn"
                    className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold"
                  >
                    <Crown className="w-4 h-4 text-[#FF9F0A]" /> Unlock on Patreon
                  </a>
                </div>
              </div>
            ) : embedSrc ? (
              <iframe
                data-testid="video-iframe"
                src={embedSrc}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
                No playable URL configured.
              </div>
            )}
          </div>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <StatusBadge status={video.status} />
                {route && (
                  <Link
                    to={`/routes/${route.id}`}
                    className="text-xs uppercase tracking-widest font-bold text-zinc-400 hover:text-[#E50914]"
                  >
                    {route.name} route
                  </Link>
                )}
                {(video.tags || []).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 px-2 py-0.5 rounded bg-white/5 border border-white/10">#{t}</span>
                ))}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-black mb-3">{video.title}</h1>
              <p className="text-zinc-400 leading-relaxed">{video.description}</p>
              <div className="mt-4 flex items-center gap-4 text-sm text-zinc-500 flex-wrap">
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {new Date(video.publish_date).toLocaleDateString()}</span>
                <span className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#E50914]" /> {(video.likes || 0).toLocaleString()}</span>
                <span className="flex items-center gap-1.5"><Eye className="w-4 h-4" /> {(video.views || 0).toLocaleString()} views</span>
              </div>
            </div>
            <button
              data-testid="video-like-btn"
              onClick={onLike}
              disabled={video.status === "patreon_exclusive"}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg font-bold transition-all border ${
                video.liked_by_user
                  ? "bg-[#E50914] border-[#E50914] text-white shadow-[0_0_24px_rgba(229,9,20,0.5)]"
                  : "bg-white/5 border-white/10 text-white hover:border-[#E50914]/50"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Heart className={`w-4 h-4 ${video.liked_by_user ? "fill-white" : ""}`} />
              {video.liked_by_user ? "Liked" : "Like"}
            </button>
            <button
              data-testid="video-watchlater-btn"
              onClick={async () => {
                if (!user) { toast.error("Login required"); return; }
                try {
                  await api.post(`/users/me/watch-later/${id}`);
                  toast.success("Watch Later updated");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg font-bold text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-white"
            >
              <Calendar className="w-4 h-4" /> Watch later
            </button>
            <button
              data-testid="video-share-btn"
              onClick={onShare}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg font-bold text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-white"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>

          {video.status === "unlock_in_progress" && (
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-2 text-[#FF9F0A] text-xs font-bold uppercase tracking-widest">
                  <Flame className="w-4 h-4" /> Unlock Progress
                </span>
                <span className="text-xs text-zinc-400">
                  {(video.likes || 0).toLocaleString()} / {video.like_goal.toLocaleString()} likes
                </span>
              </div>
              <ProgressBar value={video.likes || 0} max={video.like_goal} testid="detail-progress" />
              <p className="mt-3 text-xs text-zinc-500">Hit the goal to unlock the uncensored version for everyone.</p>
            </div>
          )}

          {/* Comments */}
          <div className="space-y-4" data-testid="comments-section">
            <h3 className="font-display text-xl font-bold">Comments ({comments.length})</h3>
            <form onSubmit={onComment} className="flex gap-2">
              <input
                data-testid="comment-input"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={user ? "Drop a comment..." : "Login to comment"}
                disabled={!user}
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm text-white placeholder:text-zinc-500 outline-none disabled:opacity-50"
              />
              <button
                data-testid="comment-submit"
                type="submit"
                disabled={!user || !commentText.trim()}
                className="px-4 py-3 rounded-xl bg-[#E50914] hover:bg-[#F6121D] disabled:opacity-50 text-white font-bold inline-flex items-center gap-2"
              >
                <Send className="w-4 h-4" /> Post
              </button>
            </form>
            <div className="space-y-3">
              <CommentList
                comments={comments}
                currentUser={user}
                onDelete={onDeleteComment}
                onReact={onReact}
                onReply={onReply}
              />
            </div>
          </div>
        </div>

        {/* Right side: related with tabs */}
        <aside className="space-y-4" data-testid="related-section">
          <div className="flex gap-2 p-1 rounded-xl bg-white/5 border border-white/10">
            <button
              data-testid="related-tab-all"
              onClick={() => setRelatedTab("all")}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors ${
                relatedTab === "all" ? "bg-[#E50914] text-white shadow-[0_0_18px_rgba(229,9,20,0.4)]" : "text-zinc-400 hover:text-white"
              }`}
            >
              All videos
            </button>
            <button
              data-testid="related-tab-route"
              onClick={() => setRelatedTab("route")}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors ${
                relatedTab === "route" ? "bg-[#E50914] text-white shadow-[0_0_18px_rgba(229,9,20,0.4)]" : "text-zinc-400 hover:text-white"
              }`}
            >
              Same Route
            </button>
          </div>
          <div className="space-y-4">
            {(relatedTab === "all" ? allVideos : sameRoute).map((v) => <VideoCard key={v.id} video={v} />)}
            {(relatedTab === "all" ? allVideos : sameRoute).length === 0 && (
              <p className="text-sm text-zinc-500">No videos to show.</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
